import ActionButton from 'components/ActionButton';
import AddSupplierModal from 'components/AddSupplierModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import { Loading } from 'components/loading/Loading';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { deleteSupplierInventoryMapping, fetchSuppliersForInventoryItemForAdmin } from 'services';
import { USER_ROLES, getPageLimit, isUserAuthorized, showError, showSuccess, updatePageLimit } from 'utils';
import styles from './index.module.scss';
import EditActionForTable from 'components/EditActionForTableData';
import { ISelectedMappingForEdit } from 'interfaces/supplierInventoryMapping';
import Currency from 'components/Currency/Currency';
import CustomLink from 'components/TableLink';

const filters: { [p: string]: any; page: number } = {
  page: 0,
};
const supplierMappingDefaultState: ISelectedMappingForEdit = { id:'', originalSupplierCost : 0, originalSupplierUnit : '', supplierName : '' };
const SupplierDetails = props => {
  const {
    inventoryItemId,
    addSupplierModalOpen,
    updateSupplierModalOpen,
    toggleModal,
    toggleUpdateModal,
    userRoles,
  } = props;
  const isUserAuthorizedToEdit = isUserAuthorized(userRoles, [USER_ROLES.PACKER, USER_ROLES.SUPERVISOR, USER_ROLES.MANAGER, USER_ROLES.ADMIN]);

  const [loading, setLoading] = useState<boolean>(false);
  const [suppliers, setSuppliers] = useState<Record<string, any>>({});
  const [selectedSupplierMapping,setSelectedSupplierMapping] = useState<ISelectedMappingForEdit>(supplierMappingDefaultState);

  const fetchSuppliersForInventoryItemFn = async () => {
    setLoading(true);
    try {
      const response = await fetchSuppliersForInventoryItemForAdmin({
        ...filters,
        id: inventoryItemId,
      });
      setSuppliers(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliersForInventoryItemFn();
  }, []);
  
  const removeSupplier = async (id: string) => {
    try {
      await deleteSupplierInventoryMapping(id);
      await fetchSuppliersForInventoryItemFn();
      showSuccess('Supplier removed successfully.');
    } catch (e) {
      showError(e);
    }
  };

  const editSupplier =  (row) => {
    setSelectedSupplierMapping({
      id:row.objectId,
      originalSupplierCost: row.cost,
      originalSupplierUnit: row.supplierUnit,
      supplierName: row.supplier.name,
    });
    toggleUpdateModal();
  };

  const columns = useMemo(() => [
    {
      name: 'SupplierID',
      selector: 'supplier.objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.supplier.objectId}
          linkTo={`/suppliers/${row.supplier.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'supplier.name',
      sortable: true,
    },
    {
      name: 'Email',
      selector: 'supplier.email',
      sortable: true,
    },
    {
      name: 'Phone',
      selector: 'supplier.phone',
      sortable: true,
    },
    {
      name: 'Supplier Mapping',
      selector: 'supplierUnit',
      sortable: true,
    },
    {
      name: 'Item Cost',
      selector: 'cost',
      sortable: true,
      // eslint-disable-next-line react/display-name
      format: (row) => (<Currency amount={row.cost} default="-" />),
    },
    {
      name: 'Action',
      selector: '',
      omit: !isUserAuthorizedToEdit,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'d-flex ml-1'}>
          <div>
            <EditActionForTable
              onClick={() => editSupplier(row)}
            />
          </div>
          <div className={'ml-4'}>
            <DeleteActionForTable
              onClick={() => removeSupplier(row.objectId)}
            />
          </div>
        </div>
      ),
    },
  ], []);

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchSuppliersForInventoryItemFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const getDataTable = () => {
    if (loading) return <Loading type={'mini'} />;
    return (
      <DataTable
        keyField="objectid"
        columns={columns}
        noHeader={true}
        data={suppliers.items}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={suppliers.total}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    );
  };

  const SectionHeader = () => {
    const ButtonText = () => <>Supplier <span className={'ml-3'}>+</span></>;
    
    return (
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Suppliers Mapping</div>
        {isUserAuthorizedToEdit && <ActionButton
          active={true}
          isLoading={false}
          onClick={toggleModal}
          text={<ButtonText />}
        />}
      </div>
    );
  };

  return (
    <div className={styles['supplier-details-container']}>
      {addSupplierModalOpen && (
        <AddSupplierModal
          isEdit={false}
          itemId={inventoryItemId}
          selectedSupplierMapping={{}}
          onSuccess={fetchSuppliersForInventoryItemFn}
          toggleModal={toggleModal}
        />
      )}
      {updateSupplierModalOpen && (
        <AddSupplierModal
          isEdit={true}
          itemId={inventoryItemId}
          selectedSupplierMapping={selectedSupplierMapping}
          onSuccess={fetchSuppliersForInventoryItemFn}
          toggleModal={toggleUpdateModal}
        />
      )}
      <SectionHeader />
      {getDataTable()}
    </div>
  );
};

export default SupplierDetails;
